import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  Box, TextField, Button, Typography,
  CircularProgress, Alert, InputAdornment, IconButton,
} from "@mui/material";
import EmailOutlinedIcon          from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon           from "@mui/icons-material/LockOutlined";
import PersonOutlinedIcon         from "@mui/icons-material/PersonOutlined";
import VisibilityOutlinedIcon     from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon  from "@mui/icons-material/VisibilityOffOutlined";
import ArrowForwardIcon           from "@mui/icons-material/ArrowForward";
import ArrowBackIcon              from "@mui/icons-material/ArrowBack";

function Register() {
  const [name,         setName]         = useState("");
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPass,     setShowPass]     = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate("/");
  }, [navigate]);

  const handleRegister = async () => {
    setError(null);
    if (!name || !email || !password || !confirmation) {
      setError("Semua field wajib diisi.");
      return;
    }
    if (password !== confirmation) {
      setError("Password dan konfirmasi password tidak cocok.");
      return;
    }
    if (password.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }
    try {
      setLoading(true);
      const response = await api.post("/register", {
        name:                  name.trim(),
        email:                 email.trim(),
        password,
        password_confirmation: confirmation,
      });
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      navigate("/");
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        setError(Object.values(errors)[0][0]);
      } else {
        setError(err.response?.data?.message || "Registrasi gagal, coba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "0.875rem",
      "&:hover fieldset":       { borderColor: "#9ca3af" },
      "&.Mui-focused fieldset": { borderColor: "#111827", borderWidth: "1.5px" },
    },
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Background foto kampus — ganti URL dengan foto LPKIA asli, misal: url('/foto-kampus.jpg') */}
      <Box sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: "url('https://images.unsplash.com/photo-1562774053-701939374585?w=1600&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        filter: "blur(6px) brightness(0.45)",
        transform: "scale(1.05)",
      }} />

      {/* Overlay gelap */}
      <Box sx={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(17,24,39,0.55) 100%)",
      }} />

      {/* Card register — lebih compact dengan py lebih kecil */}
      <Box sx={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: 400,
        backgroundColor: "rgba(255,255,255,0.97)",
        borderRadius: "20px",
        border: "1px solid rgba(255,255,255,0.3)",
        px: { xs: 3, sm: 4 },
        py: { xs: 2.5, sm: 3 },
        boxShadow: "0 24px 64px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.2)",
        mx: 2,
        my: 2,
      }}>

        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
          <Box component="img" src="/lpkia-logo2.png" alt="LPKIA"
            sx={{ height: 32, width: "auto", objectFit: "contain" }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "0.9rem", color: "#111827", lineHeight: 1.2 }}>
              BAS-LPKIA
            </Typography>
            <Typography sx={{ fontSize: "0.62rem", color: "#6b7280", lineHeight: 1.2 }}>
              Sistem Pengadaan
            </Typography>
          </Box>
        </Box>

        {/* ← Kembali */}
        <Box onClick={() => navigate("/")} sx={{
          display: "flex", alignItems: "center", gap: 0.5,
          color: "#9ca3af", cursor: "pointer", mb: 2,
          width: "fit-content",
          "&:hover": { color: "#111827" },
          transition: "color 0.15s",
        }}>
          <ArrowBackIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>Kembali</Typography>
        </Box>

        <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.03em", mb: 0.3 }}>
          Buat Akun
        </Typography>
        <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", mb: 2 }}>
          Daftar untuk mengajukan pengadaan
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}
            sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", py: 0.5 }}>
            {error}
          </Alert>
        )}

        {/* Nama + Email — sejajar 2 kolom */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>
              Nama Lengkap
            </Typography>
            <TextField fullWidth size="small" placeholder="Nama Anda"
              value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
              InputProps={{ startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                </InputAdornment>
              )}}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>
              Email
            </Typography>
            <TextField fullWidth size="small" placeholder="nama@email.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
              InputProps={{ startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                </InputAdornment>
              )}}
              sx={fieldSx}
            />
          </Box>
        </Box>

        {/* Password + Konfirmasi — sejajar 2 kolom */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>
              Password
            </Typography>
            <TextField fullWidth size="small" type={showPass ? "text" : "password"}
              placeholder="Min. 8 karakter"
              value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPass(!showPass)} edge="end">
                      {showPass
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                        : <VisibilityOutlinedIcon    sx={{ fontSize: 16, color: "#9ca3af" }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", mb: 0.6 }}>
              Konfirmasi Password
            </Typography>
            <TextField fullWidth size="small" type={showConfirm ? "text" : "password"}
              placeholder="Ulangi password"
              value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRegister()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end">
                      {showConfirm
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                        : <VisibilityOutlinedIcon    sx={{ fontSize: 16, color: "#9ca3af" }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>
        </Box>

        {/* Submit */}
        <Button type="button" fullWidth variant="contained" onClick={handleRegister} disabled={loading}
          endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: 17 }} />}
          sx={{
            borderRadius: "8px", textTransform: "none", fontSize: "0.9rem", fontWeight: 700,
            py: 1.3, backgroundColor: "#111827", boxShadow: "none",
            "&:hover": { backgroundColor: "#1f2937", boxShadow: "0 4px 16px rgba(0,0,0,0.15)" },
            "&:disabled": { backgroundColor: "#e5e7eb" },
            transition: "all 0.2s",
          }}>
          {loading ? <CircularProgress size={18} sx={{ color: "#9ca3af" }} /> : "Daftar Sekarang"}
        </Button>

        <Typography sx={{ fontSize: "0.8rem", color: "#6b7280", textAlign: "center", mt: 2 }}>
          Sudah punya akun?{" "}
          <Box component="span"
            onClick={() => navigate("/login")}
            sx={{ color: "#111827", fontWeight: 700, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
            Masuk di sini
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

export default Register;
